/**
 * Page-about template ESM (Phase 10).
 *
 * Replaces theme/js/templates/page-about.js. Drives the jean-anatomy
 * accordion in `snippets/jean-anatomy.liquid`:
 *
 *   - Trigger button (`.js-jean-anatomy-target[data-target-section]`)
 *     toggles the matching info panel (`.js-anatomy-info-section#…`),
 *     fading it in or out at 300ms with the shared easing token.
 *   - Opening one panel closes any other already-visible panel first
 *     (so the layout never holds two open at once).
 *   - The trigger gets `.active` while its panel is open.
 *   - The in-panel close affordances (`.js-close-jean-anatomy-section`)
 *     close the enclosing panel.
 *
 * Source path: theme/js/templates/page-about.esm.js
 * Built name : ./page-about.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-04
 * Model: Claude Opus 4.7
 *
 * [PageAbout]
 */

import { log } from './log.js';
import { fadeIn, fadeOut } from './core-anim.js';
import { getEasing } from './easing.js';

const TRIGGER_SELECTOR = '.js-jean-anatomy-target';
const INFO_SECTION_SELECTOR = '.js-anatomy-info-section';
const CLOSE_SELECTOR = '.js-close-jean-anatomy-section';
const ACTIVE_CLASS = 'active';
const FADE_MS = 300;

function isVisible(el) {
  if (!el) return false;
  // Matches the jQuery `:visible` semantics the IIFE used: any layout box.
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
}

function openSection(section) {
  if (!section) return;
  const id = section.id;
  document
    .querySelectorAll(`${INFO_SECTION_SELECTOR}:not(#${id})`)
    .forEach((other) => {
      if (isVisible(other)) closeSection(other);
    });
  document
    .querySelectorAll(`${TRIGGER_SELECTOR}[data-target-section="${id}"]`)
    .forEach((trigger) => trigger.classList.add(ACTIVE_CLASS));
  fadeIn(section, { duration: FADE_MS, easing: getEasing() });
}

function closeSection(section) {
  if (!section) return;
  const id = section.id;
  document
    .querySelectorAll(`${TRIGGER_SELECTOR}[data-target-section="${id}"]`)
    .forEach((trigger) => trigger.classList.remove(ACTIVE_CLASS));
  fadeOut(section, { duration: FADE_MS, easing: getEasing() });
}

function handleClick(event) {
  const trigger = event.target.closest(TRIGGER_SELECTOR);
  if (trigger) {
    event.preventDefault();
    const id = trigger.getAttribute('data-target-section');
    if (!id) return;
    const section = document.getElementById(id);
    if (!section) return;
    if (trigger.classList.contains(ACTIVE_CLASS)) {
      closeSection(section);
    } else {
      openSection(section);
    }
    return;
  }
  const close = event.target.closest(CLOSE_SELECTOR);
  if (close) {
    event.preventDefault();
    const section = close.closest(INFO_SECTION_SELECTOR);
    closeSection(section);
  }
}

document.addEventListener('click', handleClick);

log('[PageAbout] registered');
